import { z } from 'zod'
import { PRODUCT_CATEGORY_IDS } from '../constants'
import { AXES, findMajor, findSub } from '../taxonomy'
import type { FactoryCapabilities } from './factory-capabilities'
import { QUOTE_STORAGE_TYPES, type QuoteRequestDoc } from './quotes'

/**
 * 공장 목록 (로드맵 1위, 2026-10-29). 운영자가 견적을 넣을 때 공장명을 손으로 적는 대신 여기서 고른다.
 * 관리자만 본다 — 앱에는 견적에 남긴 공장명·소재지·인증만 간다 (게재 동의 전이라 목록 자체는 공개하지 않는다).
 *
 * 영업 리스트 전체를 넣고(대표 결정 10/30) 파트너만 필터로 추린다. 단계(관계):
 *   lead = 영업 리스트(미접촉·답 없음), replied = 사람 회신, negotiating = 조건 협의 중, partner = 협력 확정,
 *   inactive = 협력 불가·운영 종료·보류, do_not_contact = 연락 금지(수신거부·수집거부 고지).
 * do_not_contact 는 발송 제외 목록의 원본이라 한번 붙으면 관리자 화면·시드 재실행으로 풀지 않는다 (서버가 막는다).
 * inactive·do_not_contact 는 견적 입력의 공장 고르기에서 빠진다
 */
export const FACTORY_STATUSES = [
  'lead',
  'replied',
  'negotiating',
  'partner',
  'inactive',
  'do_not_contact',
] as const
export type FactoryStatus = (typeof FACTORY_STATUSES)[number]

/** 견적에 고를 수 없는 단계 */
export const FACTORY_CLOSED_STATUSES: readonly FactoryStatus[] = ['inactive', 'do_not_contact']

/**
 * 공장 분류 하나 = 레시피캣 taxonomy 의 대(·중)분류 키 (식품 축 product, 메뉴 축 menu). subId 가 null 이면 대분류만.
 * 샘플 거래(#142)의 카테고리 묶음이 이 키를 쓴다
 */
export const factoryTaxonomyKeySchema = z
  .object({
    axis: z.enum(AXES),
    majorId: z.string().min(1).max(40),
    subId: z.string().min(1).max(80).nullable().default(null),
  })
  .refine(
    (k) =>
      k.subId === null
        ? findMajor(k.axis, k.majorId) !== undefined
        : findSub(k.axis, k.majorId, k.subId) !== undefined,
    { message: 'Unknown taxonomy key' },
  )
export type FactoryTaxonomyKey = z.infer<typeof factoryTaxonomyKeySchema>

/**
 * 분류 신뢰도. high·medium·low 는 품목 글자에서 자동 분류한 것(classifyFactoryTaxonomy), manual 은 관리자가 고친 것
 */
export const FACTORY_TAXONOMY_CONFIDENCES = ['high', 'medium', 'low', 'manual'] as const
export type FactoryTaxonomyConfidence = (typeof FACTORY_TAXONOMY_CONFIDENCES)[number]

/** 관리자가 공장을 만들거나 고칠 때 보내는 값. 시드 도구(apps/api/scripts/factory-seed)도 같은 스키마로 검사한다 */
export const factoryInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** ISO 3166-1 alpha-2 (KR, JP, US …) */
  country: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/)
    .default('KR'),
  /** 시·군 단위 소재지 (견적의 factoryLocation 으로 복사된다) */
  region: z.string().trim().max(80).default(''),
  categoryIds: z.array(z.enum(PRODUCT_CATEGORY_IDS)).max(PRODUCT_CATEGORY_IDS.length).default([]),
  /** 받을 수 있는 보관 방식. 비어 있으면 아직 모름 (맞음·안 맞음을 따지지 않는다) */
  storage: z.array(z.enum(QUOTE_STORAGE_TYPES)).max(QUOTE_STORAGE_TYPES.length).default([]),
  /** 공장 인증. 견적의 factoryCertifications 와 같은 꼴이라 그대로 복사된다 */
  certifications: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
  /** 만들 수 있는 품목 (공장이 적어 준 말 그대로) */
  items: z.string().trim().max(500).default(''),
  /** 최소 주문량 원문 — kg·포·병처럼 단위가 공장마다 달라 숫자로 바꾸지 않는다 */
  moqNote: z.string().trim().max(300).default(''),
  /** 개발·생산 기간 원문 */
  leadTimeNote: z.string().trim().max(300).default(''),
  website: z.string().trim().max(300).default(''),
  /** 회사 대표번호만. 개인 휴대폰은 넣지 않는다 */
  phone: z.string().trim().max(40).default(''),
  /** 회사 대표 메일만 (info@, sales@ …). 개인 메일은 넣지 않는다 */
  email: z.string().trim().max(120).default(''),
  /** 이메일이 없는 공장의 홈페이지 문의폼 주소 */
  contactFormUrl: z.string().trim().max(300).default(''),
  status: z.enum(FACTORY_STATUSES).default('lead'),
  taxonomyKeys: z.array(factoryTaxonomyKeySchema).max(12).default([]),
  /** 분류가 없으면 null */
  taxonomyConfidence: z.enum(FACTORY_TAXONOMY_CONFIDENCES).nullable().default(null),
  /** 자동 분류가 애매함 — 관리자 확인 필요 */
  taxonomyNeedsReview: z.boolean().default(false),
  /** 운영자 메모 — 포장·결제 조건, 회신 단계 등 */
  notes: z.string().trim().max(2000).default(''),
  /**
   * 이 공장의 공장 포털 계정 PC 원장 id (manufacturing_factory_accounts.entity_id). 이어 두면 그 계정이 샘플 공장 포털에서
   * 이 공장의 샘플 제안·주문을 본다. 비우면 null (2026-10-30 '공장 정보 하나로')
   */
  manufacturingEntityId: z
    .string()
    .trim()
    .max(100)
    .nullable()
    .default(null)
    .transform((v) => v || null),
})
export type FactoryInput = z.infer<typeof factoryInputSchema>

/** GET /admin/factories 의 한 줄 */
export interface FactoryView extends FactoryInput {
  id: string
  /** 시드로 넣은 행의 출처 키 (예: fm:d:sandeulfood.com). 관리자가 만든 행은 null */
  sourceRef: string | null
  /** 설비·공정별 단가·최소 주문량 (로드맵 8번). 회신을 반영하기 전에는 null */
  capabilities: FactoryCapabilities | null
  createdAt: string
  updatedAt: string
}

export interface AdminFactoryListResponse {
  items: FactoryView[]
}

/** 회사 이름 비교용: 법인 표기·공백·문장부호를 지운다 ('(주)한빛 식품' = '한빛식품') */
export function normalizeFactoryName(name: string): string {
  return name
    .normalize('NFKC')
    .toLowerCase()
    .replace(/㈜|\(주\)|주식회사|\(유\)|유한회사|co\.?,?\s*ltd\.?|inc\.?|corp\.?/g, '')
    .replace(/[\s\p{P}\p{S}]/gu, '')
}

/**
 * 공장명을 손으로 적은 옛 견적을 공장 목록과 잇는다. 이름이 같은 공장이 정확히 하나일 때만 돌려준다 —
 * 같은 이름이 둘이면 어느 쪽인지 사람이 골라야 한다
 */
export function findFactoryByName<T extends Pick<FactoryView, 'name'>>(
  name: string,
  factories: readonly T[],
): T | null {
  const key = normalizeFactoryName(name)
  if (!key) return null
  const same = factories.filter((f) => normalizeFactoryName(f.name) === key)
  return same.length === 1 ? same[0]! : null
}

const certKey = (s: string) =>
  s
    .normalize('NFKC')
    .toUpperCase()
    .replace(/[\s\-_.]/g, '')

export interface FactoryRequestMatch<T> {
  factory: T
  /** 요청 제품 분류를 공장이 만든다 */
  categoryMatch: boolean
  /** 요청 보관 방식을 받는다. null = 공장 보관 방식을 아직 모름 */
  storageMatch: boolean | null
  /** 요청이 원한 인증 중 공장 목록에 없는 것 */
  missingCertifications: string[]
}

type MatchFields = Pick<FactoryView, 'categoryIds' | 'storage' | 'certifications' | 'status'>

/** 같은 조건이면 관계가 가까운 공장 먼저 */
const STATUS_RANK: Record<FactoryStatus, number> = {
  partner: 0,
  negotiating: 1,
  replied: 2,
  lead: 3,
  inactive: 4,
  do_not_contact: 4,
}

/**
 * 견적 요청에 맞는 공장 순서. 중단·연락 금지 공장은 뺀다.
 * 분류가 맞는 공장 → 관계 단계(파트너 먼저) → 보관 방식이 맞는 공장(모르면 그다음) → 빠진 인증이 적은 공장 순.
 * 같으면 들어온 순서를 지킨다
 */
export function rankFactoriesForRequest<T extends MatchFields>(
  factories: readonly T[],
  request: Pick<QuoteRequestDoc, 'categoryId' | 'storage' | 'certifications'>,
): FactoryRequestMatch<T>[] {
  const storageRank = (m: boolean | null) => (m === true ? 0 : m === null ? 1 : 2)
  return factories
    .filter((f) => !FACTORY_CLOSED_STATUSES.includes(f.status))
    .map((factory) => {
      const have = factory.certifications.map(certKey)
      return {
        factory,
        categoryMatch: factory.categoryIds.includes(request.categoryId),
        storageMatch:
          factory.storage.length === 0 ? null : factory.storage.includes(request.storage),
        missingCertifications: request.certifications.filter((want) => {
          const w = certKey(want)
          return !have.some((h) => h.includes(w) || w.includes(h))
        }),
      }
    })
    .map((m, i) => ({ m, i }))
    .sort(
      (a, b) =>
        Number(b.m.categoryMatch) - Number(a.m.categoryMatch) ||
        STATUS_RANK[a.m.factory.status] - STATUS_RANK[b.m.factory.status] ||
        storageRank(a.m.storageMatch) - storageRank(b.m.storageMatch) ||
        a.m.missingCertifications.length - b.m.missingCertifications.length ||
        a.i - b.i,
    )
    .map(({ m }) => m)
}
